import * as THREE from 'three';
import { ShaderManager } from './shadermanager';
import { TextureManager } from './texturemanager';
import { MultiWait } from '../util/multiwait';
import { EffectManager } from '../effects/effectmanager';
import { AnimationData } from '../loaders/zmo';
import { ObjectAnimator } from '../animators/objectanimator';
import { ModelList } from '../loaders/zsc';
import { Mesh } from '../loaders/zms';

/**
 * @constructor
 */
export class ModelListManager {
    data: any;
    meshes: any;
    materials: any;

    constructor(data: any) {
        this.data = data;
        this.meshes = {};
        this.materials = {};
    }

    _createMesh(meshIdx: any, callback: any, onError?: (err: any) => void) {
        const existingMesh = this.meshes[meshIdx];
        if (existingMesh) {
            if (existingMesh.mesh) {
                callback(existingMesh.mesh);
            } else if (existingMesh.failed) {
                if (onError) onError(existingMesh.failed);
            } else {
                existingMesh.waiters.push(callback);
                if (onError) existingMesh.errorWaiters.push(onError);
            }
            return;
        }

        const newMesh = {
            mesh: null as any,
            failed: null as any,
            waiters: [callback],
            errorWaiters: onError ? [onError] : [],
        };
        this.meshes[meshIdx] = newMesh;

        const meshPath = this.data.meshes[meshIdx];
        Mesh.load(meshPath, (geometry: any) => {
            newMesh.mesh = geometry;
            for (let i = 0; i < newMesh.waiters.length; ++i) {
                newMesh.waiters[i](geometry);
            }
            newMesh.waiters = [];
        }, (err: any) => {
            newMesh.failed = err || new Error('Failed to load mesh ' + meshPath);
            for (const errWaiter of newMesh.errorWaiters) errWaiter(newMesh.failed);
            newMesh.waiters = [];
            newMesh.errorWaiters = [];
        });
    }

    _createMaterialLMOnly(materialIdx: any, lmData: any) {
        let zscMat = this.data.materials[materialIdx];

        let lmTexture = lmData.texture;
        let objScale = 1 / lmData.objectsPerRow;
        let rowNum = Math.floor(lmData.objectIndex / lmData.objectsPerRow);
        let colNum = lmData.objectIndex % lmData.objectsPerRow;

        let newMaterial = ShaderManager.get("staticobj_lmonly").clone();
        newMaterial.uniforms = {
            texture1: { type: "t", value: lmTexture },
            vLmOffset: {
                type: "v2",
                value: new THREE.Vector2(colNum * objScale, rowNum * objScale),
            },
            vLmScale: {
                type: "v2",
                value: new THREE.Vector2(objScale, objScale),
            },
        };

        if (zscMat.twoSided) {
            newMaterial.side = THREE.DoubleSide;
        }

        newMaterial.opacity = zscMat.alpha;
        newMaterial.depthTest = zscMat.depthTestEnabled;
        newMaterial.depthWrite = zscMat.depthWriteEnabled;

        return newMaterial;
    }

    _createMaterialWithLightmap(materialIdx: any, lmData: any) {
        let zscMat = this.data.materials[materialIdx];
        let texture = TextureManager.load(zscMat.texturePath);

        const lmTexture = lmData.texture;
        const objScale = 1 / lmData.objectsPerRow;
        const rowNum = Math.floor(lmData.objectIndex / lmData.objectsPerRow);
        const colNum = lmData.objectIndex % lmData.objectsPerRow;

        let newMaterial = ShaderManager.get("staticobj").clone();
        newMaterial.uniforms = {
            texture1: { type: "t", value: texture },
            texture2: { type: "t", value: lmTexture },
            vLmOffset: {
                type: "v2",
                value: new THREE.Vector2(colNum * objScale, rowNum * objScale),
            },
            vLmScale: {
                type: "v2",
                value: new THREE.Vector2(objScale, objScale),
            },
            dayNightCycle: { type: "f", value: 0.0 },
        };

        if (zscMat.twoSided) {
            newMaterial.side = THREE.DoubleSide;
        }

        if (zscMat.alphaEnabled) {
            newMaterial.transparent = true;

            if (zscMat.alphaTestEnabled) {
                newMaterial.alphaTest = zscMat.alphaRef / 256;
            }
        }

        newMaterial.opacity = zscMat.alpha;
        newMaterial.depthTest = zscMat.depthTestEnabled;
        newMaterial.depthWrite = zscMat.depthWriteEnabled;

        return newMaterial;
    }

    _createMaterial(materialIdx: any, onError?: (err: any) => void) {
        const zscMat = this.data.materials[materialIdx];

        const texture = TextureManager.load(zscMat.texturePath, undefined, onError);

        const newMaterial = new THREE.MeshLambertMaterial({
            color: 0xffffff,
            map: texture,
        });

        if (zscMat.twoSided) {
            newMaterial.side = THREE.DoubleSide;
        }

        if (zscMat.alphaEnabled) {
            newMaterial.transparent = true;

            if (zscMat.alphaTestEnabled) {
                newMaterial.alphaTest = zscMat.alphaRef / 255;
            }
        }

        newMaterial.opacity = zscMat.alpha;
        newMaterial.depthTest = zscMat.depthTestEnabled;
        newMaterial.depthWrite = zscMat.depthWriteEnabled;

        return newMaterial;
    }

    createForStatic(modelIdx: any, lightmap: any, lmIdx: any, callback: any) {
        const model = this.data.models[modelIdx];
        if (!model) {
            console.warn("Model not found for id", modelIdx, this.data);
            callback();
            return;
        }

        const modelObj = new THREE.Object3D();
        modelObj.visible = false;

        const partMeshs: any = [];
        const waitAll = new MultiWait();

        for (let i = 0; i < model.parts.length; ++i) {
            ((partIdx, part, partCallback) => {
                let lmData = null;
                if (lightmap) {
                    lmData = lightmap.getDataForPart(lmIdx, i);
                }
                let material: any = null;

                if (!lmData) {
                    material = this._createMaterial(part.materialIdx);
                } else if (!config.lmonly) {
                    material = this._createMaterialWithLightmap(
                        part.materialIdx,
                        lmData
                    );
                } else {
                    material = this._createMaterialLMOnly(
                        part.materialIdx,
                        lmData
                    );
                }
                this._createMesh(part.meshIdx, (geometry: any) => {
                    const partMesh = new THREE.Mesh(geometry, material);
                    (partMesh as any).collisionMode = part.collisionMode;
                    partMeshs[partIdx] = partMesh;

                    if (part.position) {
                        partMesh.position.copy(part.position);
                    }

                    if (part.rotation) {
                        partMesh.quaternion.copy(part.rotation);
                    }

                    if (part.scale) {
                        partMesh.scale.copy(part.scale);
                    }

                    if (part.animPath) {
                        AnimationData.load(
                            part.animPath,
                            function (zmoData: any) {
                                const anim = new ObjectAnimator(
                                    partMeshs[partIdx],
                                    zmoData
                                );
                                anim.play();
                            }
                        );
                    }

                    partCallback();
                });
            })(i, model.parts[i], waitAll.one());
        }

        waitAll.wait(() => {
            for (let i = 0; i < model.effects.length; ++i) {
                const effectData = model.effects[i];
                if (
                    effectData.effectIdx < 0 ||
                    effectData.effectIdx > this.data.effects.length
                ) {
                    console.log(
                        "Warning, model effect had invalid effectIdx " +
                            effectData.effectIdx
                    );
                    continue;
                }
                /* types are:
        POINT_EFFECT_NORMAL = 0,
         POINT_EFFECT_DAYNNIGHT = 1,
         POINT_LIGHT_CONTAINER = 2,
        */
                const effectPath = this.data.effects[effectData.effectIdx];
                EffectManager.loadEffect(effectPath, (effect: any) => {
                    if (effect) {
                        effect.rootObj.position.copy(effectData.position);
                        effect.rootObj.quaternion.copy(effectData.rotation);
                        effect.rootObj.scale.copy(effectData.scale);
                        effect.rootObj.collisionMode =
                            ModelList.Model.Part.COLLISION_MODE.NONE;
                        if (effectData.parent === 0) {
                            modelObj.add(effect.rootObj);
                        } else {
                            partMeshs[effectData.parent - 1].add(
                                effect.rootObj
                            );
                        }
                        effect.play();
                    }
                });
            }

            for (let i = 0; i < partMeshs.length; ++i) {
                const part = model.parts[i];

                if (i === 0) {
                    modelObj.add(partMeshs[i]);
                } else {
                    partMeshs[part.parent - 1].add(partMeshs[i]);
                }
            }
            modelObj.visible = true;
            if (callback) {
                callback();
            }
        });

        return modelObj;
    }

    /**
     * This is a helper to allow a ModelListManager to be used by the DataManager.
     * @param path
     * @param callback
     */
    static load(path: any, callback: any) {
        ModelList.load(path, function (data: any) {
            callback(new ModelListManager(data));
        });
    }
}

